import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button } from '@lms/ui';
import { filterVisibleTabs } from '@/config/siteAdminNavigation';
import { ProfileField } from '@/components/profile/ProfileField';
import { ProfileAvatarBlock } from '@/components/profile/ProfileAvatarBlock';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { ProfileOverview, type ProfileDetail } from '@/features/profile/ProfileOverview';

interface ProfileForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface PasswordForm {
  current_password: string;
  password: string;
  password_confirmation: string;
}

type Screen = 'view' | 'edit' | 'password';

const SCREEN_HEADERS: Record<Exclude<Screen, 'view'>, { title: string; description: string }> = {
  edit: {
    title: 'Editar perfil',
    description: 'Actualice su nombre, correo y foto de perfil',
  },
  password: {
    title: 'Contraseña',
    description: 'Use una contraseña segura y única',
  },
};

function profileInitials(firstName: string | null, lastName: string | null, name: string) {
  const full = [firstName, lastName].filter(Boolean).join(' ').trim() || name;
  return full
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function ProfilePage() {
  const queryClient = useQueryClient();
  const { canAccessAdmin, hasPermission, adminTabOptions } = usePermissions();
  const [screen, setScreen] = useState<Screen>('view');
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const adminBackTab = useMemo(() => {
    const keys = Object.keys(filterVisibleTabs(hasPermission, adminTabOptions));
    return keys[0] ?? 'appearance';
  }, [hasPermission, adminTabOptions]);

  const backTo = canAccessAdmin ? `/administracion?tab=${adminBackTab}` : '/dashboard';

  const profileQuery = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: () => api<ProfileDetail>('/users/me'),
  });

  const profile = profileQuery.data;

  const profileForm = useForm<ProfileForm>({
    values: {
      firstName: profile?.firstName ?? profile?.name?.split(' ')[0] ?? '',
      lastName: profile?.lastName ?? profile?.name?.split(' ').slice(1).join(' ') ?? '',
      email: profile?.email ?? '',
      phone: profile?.phone ?? '',
    },
  });

  const passwordForm = useForm<PasswordForm>();

  const profileMutation = useMutation({
    mutationFn: (values: ProfileForm) =>
      api('/users/me', {
        method: 'PATCH',
        body: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone || undefined,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const passwordMutation = useMutation({
    mutationFn: (values: PasswordForm) =>
      api('/users/me/password', { method: 'PUT', body: values }),
    onSuccess: () => {
      passwordForm.reset();
    },
  });

  if (screen === 'view') {
    return (
      <ProfileOverview
        backTo={backTo}
        onEdit={() => setScreen('edit')}
        onPassword={() => setScreen('password')}
      />
    );
  }

  const header = SCREEN_HEADERS[screen];
  const avatarInitials = profileInitials(profile?.firstName ?? null, profile?.lastName ?? null, profile?.name ?? '');
  const avatarProfile = profile
    ? {
        avatar: profile.avatarUrl ?? null,
        firstName: profile.firstName ?? null,
        lastName: profile.lastName ?? null,
        name: profile.name,
      }
    : undefined;

  return (
    <div className="lms-profile lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink onBack={() => setScreen('view')} label="Volver a mi perfil" />
        <div className="min-w-0 flex-1">
          <h1 className="mb-2 text-3xl font-semibold text-dash-primary md:text-4xl">{header.title}</h1>
          <p className="text-dash-on-surface-variant">{header.description}</p>
        </div>
      </div>

      {profileQuery.isLoading && <p className="text-sm text-dash-on-surface-variant">Cargando perfil…</p>}
      {profileQuery.isError && (
        <Alert variant="danger" className="mb-6">
          {(profileQuery.error as Error).message || 'No se pudo cargar el perfil.'}
        </Alert>
      )}

      {screen === 'edit' && !profileQuery.isLoading && (
        <div className="rounded-xl border border-dash-outline-variant bg-white p-6 shadow-sm transition-shadow hover:shadow-md md:p-8">
          {profileMutation.isSuccess && (
            <Alert variant="success" className="mb-6">
              Perfil actualizado correctamente.
            </Alert>
          )}
          {profileMutation.error && (
            <Alert variant="danger" className="mb-6">
              {(profileMutation.error as Error).message}
            </Alert>
          )}

          <form
            onSubmit={profileForm.handleSubmit((values) => profileMutation.mutate(values))}
            className="space-y-10"
          >
            <div className="flex flex-col items-center gap-8 border-b border-dash-outline-variant pb-10 sm:flex-row sm:items-start">
              <ProfileAvatarBlock profile={avatarProfile} avatarInputRef={avatarInputRef} initials={avatarInitials} />
            </div>

            <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
              <ProfileField label="Nombre" required {...profileForm.register('firstName', { required: true })} />
              <ProfileField label="Apellidos" required {...profileForm.register('lastName', { required: true })} />
              <ProfileField
                label="Correo electrónico"
                type="email"
                required
                wrapperClassName="md:col-span-2"
                {...profileForm.register('email', { required: true })}
              />
              <ProfileField
                label="Teléfono"
                type="tel"
                placeholder="+56 9 1234 5678"
                wrapperClassName="md:col-span-2"
                {...profileForm.register('phone')}
              />
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-end gap-4 border-t border-dash-outline-variant pt-8">
              <button
                type="button"
                onClick={() => setScreen('view')}
                className="px-6 py-3 text-sm font-bold text-dash-on-surface-variant transition-colors hover:text-dash-primary"
              >
                Cancelar
              </button>
              <Button type="submit" loading={profileMutation.isPending} className="!rounded-lg !px-8 !py-3 !font-bold shadow-md">
                <span className="material-symbols-outlined text-lg" aria-hidden="true">
                  check_circle
                </span>
                Guardar cambios
              </Button>
            </div>
          </form>
        </div>
      )}

      {screen === 'password' && (
        <div className="rounded-xl border border-dash-outline-variant bg-white p-6 shadow-sm md:p-8">
          {passwordMutation.isSuccess && (
            <Alert variant="success" className="mb-6">
              Contraseña actualizada correctamente.
            </Alert>
          )}
          {passwordMutation.error && (
            <Alert variant="danger" className="mb-6">
              {(passwordMutation.error as Error).message}
            </Alert>
          )}
          <form onSubmit={passwordForm.handleSubmit((values) => passwordMutation.mutate(values))} className="space-y-6">
            <ProfileField
              label="Contraseña actual"
              type="password"
              required
              autoComplete="current-password"
              {...passwordForm.register('current_password', { required: true })}
            />
            <ProfileField
              label="Nueva contraseña"
              type="password"
              required
              autoComplete="new-password"
              {...passwordForm.register('password', { required: true, minLength: 8 })}
            />
            <ProfileField
              label="Confirmar nueva contraseña"
              type="password"
              required
              autoComplete="new-password"
              {...passwordForm.register('password_confirmation', { required: true })}
            />
            <div className="flex justify-end border-t border-dash-outline-variant pt-8">
              <Button type="submit" loading={passwordMutation.isPending} className="!rounded-lg !px-8 !py-3 !font-bold">
                Actualizar contraseña
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
